// ABOUTME: Orchestration hook for the Retarder entity — combines config query, mutations, and form state.
// ABOUTME: Returns a uniform interface consumed by CountryConfigurationsEntity to keep the page component thin.

import { useCallback, useMemo, useRef } from 'react'
import { useRetarderConfig } from './useRetarderConfig'
import { useCreateRetarder, useCreateRetarderWithScope, useUpdateRetarder, useDeleteRetarder } from './useRetarderMutations'
import { RetarderConfig, RetarderFormValues, RetarderOverrides } from '../types/retarder'
import { EntityActionResult } from '../context/EntityFormContext'
import type { PendingScopeRequest } from '../context/EntityFormContext'
import { getConfigScopeId, toPendingScopeConfig } from '../utils/pendingScope'

interface UseRetarderEntityParams {
  countryId: string | undefined
  businessLine: string | undefined
  scopeId: string | undefined
  pendingScope?: PendingScopeRequest | undefined
  rootScopeId?: string | undefined
}

interface EntityActions {
  handleSave: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleReset: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleValuesChange: (values: RetarderFormValues) => void
}

interface UseRetarderEntityResult {
  config: RetarderConfig | undefined
  isLoading: boolean
  error: Error | null
  isSaving: boolean
  actions: EntityActions
}

/**
 * Computes which fields are overridden by comparing submitted form values against the inherited
 * parent values. Pre-existing override flags are intentionally ignored so reverting a field to
 * the parent value correctly yields hasAnyOverride === false and triggers the delete path.
 * Exported for testing.
 */
export const computeRetarderOverrides = (
  values: RetarderFormValues,
  config: RetarderConfig
): RetarderOverrides => {
  const parent = config.parentValues
  return {
    uom: values.uomId !== parent.uomId,
    min: values.min !== parent.min,
    max: values.max !== parent.max,
    step: values.step !== parent.step,
  }
}

export const useRetarderEntity = ({
  countryId,
  businessLine,
  scopeId,
  pendingScope,
  rootScopeId,
}: UseRetarderEntityParams): UseRetarderEntityResult => {
  const configScopeId = getConfigScopeId(scopeId, pendingScope, rootScopeId)
  const { data: rawConfig, isLoading, error } = useRetarderConfig(countryId, businessLine, configScopeId, rootScopeId)
  const config = useMemo(() => toPendingScopeConfig(rawConfig, pendingScope, (cfg) => ({
    min: cfg.min,
    max: cfg.max,
    step: cfg.step,
    uom: cfg.uom,
    uomId: cfg.uomId,
  })), [rawConfig, pendingScope])

  const createMutation = useCreateRetarder(countryId, businessLine)
  const createWithScopeMutation = useCreateRetarderWithScope(countryId, businessLine)
  const updateMutation = useUpdateRetarder(countryId, businessLine)
  const deleteMutation = useDeleteRetarder(countryId, businessLine)

  const formValuesRef = useRef<RetarderFormValues | null>(null)
  const configIdRef = useRef<string | undefined>(config?.id)
  const mutationsRef = useRef({ createMutation, createWithScopeMutation, updateMutation, deleteMutation })
  const configRef = useRef<RetarderConfig | undefined>(config)
  const pendingScopeRef = useRef<PendingScopeRequest | undefined>(pendingScope)

  configIdRef.current = config?.id
  configRef.current = config
  pendingScopeRef.current = pendingScope
  mutationsRef.current = { createMutation, createWithScopeMutation, updateMutation, deleteMutation }

  const handleValuesChange = useCallback((values: RetarderFormValues) => {
    formValuesRef.current = values
  }, [])

  const handleSave = useCallback(
    (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
      if (!formValuesRef.current || !configRef.current) return
      const values = formValuesRef.current
      const overrides = computeRetarderOverrides(values, configRef.current)
      const hasAnyOverride = Object.values(overrides).some(Boolean)
      const scopeToCreate = pendingScopeRef.current

      if (scopeToCreate) {
        if (!hasAnyOverride) {
          onSuccess({})
          return
        }
        const uomId = values.uomId ?? configRef.current?.uomId
        mutationsRef.current.createWithScopeMutation.mutate({ scope: scopeToCreate, values, uomId }, { onSuccess: (result) => onSuccess({ wasOverriding: true, createdScopeId: result?.scopeId }), onError: (error) => onError?.(error) })
        return
      }

      if (!scopeId) return

      if (!hasAnyOverride && scopeId !== rootScopeId) {
        if (configIdRef.current) {
          mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
        } else {
          onSuccess({ wasDelete: true })
        }
        return
      }

      const uomId = values.uomId ?? configRef.current?.uomId
      const isNew = !configIdRef.current
      const mutate = isNew ? mutationsRef.current.createMutation.mutate : mutationsRef.current.updateMutation.mutate
      mutate({ scopeId, values, uomId }, { onSuccess: () => onSuccess({ wasOverriding: true }), onError: (error) => onError?.(error) })
    },
    [scopeId, rootScopeId]
  )

  const handleReset = useCallback(
    (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
      if (!scopeId) return
      mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
    },
    [scopeId]
  )

  const actions = useMemo(
    () => ({ handleSave, handleReset, handleValuesChange }),
    [handleSave, handleReset, handleValuesChange]
  )

  const isSaving = createMutation.isPending || createWithScopeMutation.isPending || updateMutation.isPending || deleteMutation.isPending

  return {
    config,
    isLoading,
    error,
    isSaving,
    actions,
  }
}
